import Box from '@mui/material/Box';
import DialogContent from '@mui/material/DialogContent';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import successImage from 'assets/svg/success-sparkles.svg?url';
import axios from 'axios';
import Button from 'components/Button/Button';
import Modal from 'components/Modal/Modal';
import Form from 'components/form';
import PhoneInput from 'components/form/PhoneInput';
import TextField from 'components/form/TextInput';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { validatePhone } from 'utils';
import { snack } from 'utils/toast';

const { FormItem } = Form;

const PortNumberModal = ({ onClose, form }) => {
  const [submitted, setSubmitted] = useState(false);
  const account = useSelector((s) => s.settings.account);
  const { getFieldDecorator, validateFields } = form;
  const [t] = useTranslation('talk');

  const onSubmit = () => {
    return validateFields()
      .then((values) => {
        return axios
          .get('https://hooks.zapier.com/hooks/catch/465525/bk7a40l', {
            params: {
              ...values,
              account_id: account.account_id,
              account_email: account.account_email,
            },
          })
          .then(() => {
            setSubmitted(true);
          })
          .catch((error) => {
            console.error('Form submission error:', error);
            snack.error(
              t(
                'failed-to-submit',
                'Failed to submit your application. Please try again.',
              ),
            );
          });
      })
      .catch((error) => {
        console.error('Form validation error:', error);
        snack.error(
          t(
            'form-validation-error',
            'Please check all required fields and try again.',
          ),
        );
      });
  };

  if (submitted) {
    return (
      <Modal open maxWidth="xs">
        <DialogContent>
          <Box p={3} textAlign="center">
            <img src={successImage} style={{ height: 200 }} />
            <Typography sx={{ mt: 5, mb: 3 }} fontWeight={600} variant="h4">
              {t('great', 'Great!')}
            </Typography>
            <Typography paragraph>
              {t(
                'application-submit-note',
                "Your application to port an existing number has been submitted. We'll be in touch soon.",
              )}
            </Typography>
            <Button variant="outlined" onClick={onClose}>
              {t('got-it', 'Got it!')}
            </Button>
          </Box>
        </DialogContent>
      </Modal>
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      maxWidth="sm"
      title={t('port-existing-no', 'Port an Existing Number')}
      actions={
        <Button color="primary" onClick={onSubmit}>
          {t('common:submit', 'Submit')}
        </Button>
      }
    >
      <Form>
        <Typography>
          {t(
            'fields-from-twillio',
            'Enter the fields to port the numbers from Twilio',
          )}
        </Typography>
        <Grid container spacing={2}>
          <Grid item md={6}>
            <FormItem>
              {getFieldDecorator('first_name', {
                rules: [
                  {
                    required: true,
                    message: t(
                      'error:first-name',
                      'Please input your First Name!',
                    ),
                  },
                ],
              })(
                <TextField
                  fullWidth
                  label={t('common:firstName', 'First Name')}
                />,
              )}
            </FormItem>
          </Grid>
          <Grid item md={6}>
            <FormItem>
              {getFieldDecorator('last_name', {
                rules: [
                  {
                    required: true,
                    message: t(
                      'error:last-name',
                      'Please input your Last Name!',
                    ),
                  },
                ],
              })(
                <TextField
                  fullWidth
                  label={t('common:lastName', 'Last Name')}
                />,
              )}
            </FormItem>
          </Grid>
        </Grid>
        <FormItem>
          {getFieldDecorator('business_name', {
            rules: [
              {
                required: true,
                message: t(
                  'error:bussiness-name',
                  'Business Name is required!',
                ),
              },
            ],
          })(
            <TextField
              fullWidth
              label={t('bussiness-name', 'Business Name')}
            />,
          )}
        </FormItem>
        <FormItem>
          {getFieldDecorator('address', {
            rules: [
              {
                required: true,
                message: t('address-validation', 'Address is required!'),
              },
            ],
          })(<TextField fullWidth label={t('common:address', 'Address')} />)}
        </FormItem>
        <Grid container spacing={2}>
          <Grid item md={4}>
            <FormItem>
              {getFieldDecorator('city', {
                rules: [
                  {
                    required: true,
                    message: t('city-validation', 'City is required!'),
                  },
                ],
              })(<TextField fullWidth label={t('common:city', 'City')} />)}
            </FormItem>
          </Grid>
          <Grid item md={4}>
            <FormItem>
              {getFieldDecorator('state', {
                rules: [
                  {
                    required: true,
                    message: t(
                      'state-validation',
                      'State/Province is required!',
                    ),
                  },
                ],
              })(
                <TextField fullWidth label={t('state', 'State / Province')} />,
              )}
            </FormItem>
          </Grid>
          <Grid item md={4}>
            <FormItem>
              {getFieldDecorator('zip', {
                rules: [
                  {
                    required: true,
                    message: t(
                      'zip-validation',
                      'ZIP/Postal Code is required!',
                    ),
                  },
                ],
              })(<TextField fullWidth label={t('zip', 'ZIP/Postal Code')} />)}
            </FormItem>
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item md={6}>
            <FormItem>
              {getFieldDecorator('phone_number', {
                rules: [
                  {
                    required: true,
                    message: t('phone-validation', 'Phone Number is required!'),
                  },
                  { validator: validatePhone },
                ],
              })(
                <PhoneInput
                  fullWidth
                  label={t('common:phone-number', 'Phone Number')}
                />,
              )}
            </FormItem>
          </Grid>
          <Grid item md={6}>
            <FormItem>
              {getFieldDecorator('service_provider', {
                rules: [
                  {
                    required: true,
                    message: t(
                      'error:service-provider',
                      'Service Provider is required!',
                    ),
                  },
                ],
              })(
                <TextField
                  fullWidth
                  label={t('service-provider', 'Service Provider')}
                />,
              )}
            </FormItem>
          </Grid>
        </Grid>
      </Form>
    </Modal>
  );
};

export default Form.create()(PortNumberModal);
